import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { ThemeProvider } from '@mui/material';
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectIsSystemDarkMode } from '@common-reducers/UserSelectors';
import { lightTheme, darkTheme } from '@common/theme';
import { ShadowDOMProvider } from '@/common/contexts/ShadowDOMContext';
import { getSharedEmotionCache, adoptSharedEmotionSheet } from '@/common/components/shared-emotion-cache';

interface ShadowDOMThemeProviderProps {
  shadowRoot: ShadowRoot;
}

/**
 * Theme Provider for Shadow DOM
 * 
 * This component configures MUI and Emotion to inject styles into a Shadow DOM
 * instead of the document head, ensuring complete style isolation.
 * 
 * Usage:
 *   <ShadowDOMThemeProvider shadowRoot={shadowRoot}>
 *     <YourMUIComponents />
 *   </ShadowDOMThemeProvider>
 */
export const ShadowDOMThemeProvider: React.FC<PropsWithChildren<ShadowDOMThemeProviderProps>> = ({ 
  children, 
  shadowRoot 
}) => {
  // Get WhatsApp dark mode state from Redux (synced by page-level listener)
  const isDarkMode = useAppSelector(selectIsSystemDarkMode);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  // Use ONE Emotion cache shared across every injected shadow root so the full
  // MUI/Emotion CSSOM is serialized + stored once instead of being duplicated
  // per root (~20×). Falls back to a legacy per-root cache only when
  // constructable stylesheets are unsupported.
  const emotionCache = useMemo(() => {
    const shared = getSharedEmotionCache();
    if (shared) return shared;
    return createCache({
      key: 'mui-shadow',
      container: shadowRoot as unknown as HTMLElement,
      prepend: true,
    });
  }, [shadowRoot]);

  // Adopt the shared constructable Emotion sheet into this shadow root, next to
  // the Tailwind styles injected by injectTailwindIntoShadowRoot. No-op (and the
  // per-root fallback cache above renders styles itself) when unsupported.
  useEffect(() => {
    return adoptSharedEmotionSheet(shadowRoot);
  }, [shadowRoot]);

  // Sync dark mode to btcss wrapper and portal container for Tailwind dark: selectors
  // This ensures Shadow DOM components can use Tailwind dark mode
  useEffect(() => {
    // Find the btcss wrapper element inside the shadow root (created by injectTailwindIntoShadowRoot)
    const btcssWrapper = shadowRoot.querySelector('.btcss');
    
    // We can use the state portalContainer directly or query it
    // The portalContainer state might not be set immediately on first render effect, 
    // but the ref callback will trigger a re-render.
    
    if (btcssWrapper instanceof HTMLElement) {
      if (isDarkMode) {
        btcssWrapper.classList.add('dark');
      } else {
        btcssWrapper.classList.remove('dark');
      }
    }
    
    if (portalContainer instanceof HTMLElement) {
      if (isDarkMode) {
        portalContainer.classList.add('dark');
      } else {
        portalContainer.classList.remove('dark');
      }
    }
  }, [isDarkMode, shadowRoot, portalContainer]);

  const theme = useMemo(() => {
    return isDarkMode ? darkTheme : lightTheme;
  }, [isDarkMode]);

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={theme}>
        <ShadowDOMProvider shadowRoot={shadowRoot} portalContainer={portalContainer}>
            {children}
            {/* 
              This container is where Radix/Shadcn components will portal their content 
              (Dropdowns, Dialogs, Tooltips, etc).
              It needs to be:
              1. Inside the Shadow DOM (so styles apply)
              2. On top of everything (z-index)
              3. Full screen (to allow positioning anywhere)
              4. Pointer-events none (so it doesn't block clicks on the app below it, 
                 but its children will have pointer-events: auto)
            */}
            <div 
                ref={setPortalContainer} 
                className="btcss-portal-container btcss" 
                style={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  width: '100%', 
                  height: '100%', 
                  pointerEvents: 'none', 
                  zIndex: 9999 
                }}
            />
        </ShadowDOMProvider>
      </ThemeProvider>
    </CacheProvider>
  );
};
